import React, { useEffect, useRef, useState } from "react";

import Box from "components/common/Box";
import Text from "components/common/Text";
import IconButton from "components/common/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";

import { marketPulseTheme as theme } from "./marketPulseTheme";

const WAVEFORM_BARS = [
  8, 14, 20, 12, 24, 16, 10, 28, 15, 22, 11, 18, 9, 25, 14, 21, 12, 17, 26, 10,
  19, 15, 11, 23, 16, 12, 27, 14, 20, 10, 16, 28, 13, 19, 17, 24, 11, 21, 22, 14,
  9, 18, 15, 25, 12, 20, 16, 10, 23, 14,
];

const PLAYBACK_RATE_STORAGE_KEY = "marketPulsePlaybackRate";

const readStoredPlaybackRate = () => {
  try {
    return localStorage.getItem(PLAYBACK_RATE_STORAGE_KEY) === "2" ? 2 : 1;
  } catch {
    return 1;
  }
};

const speedSegmentSx = {
  display: "inline-flex",
  alignItems: "stretch",
  height: 37,
  borderRadius: "14px",
  backgroundColor: theme.controlBtnBg,
  border: theme.controlBtnBorder,
  overflow: "hidden",
  flexShrink: 0,
};

const speedOptionSx = (active) => ({
  minWidth: 44,
  px: 1.25,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  userSelect: "none",
  backgroundColor: active ? "rgba(255, 255, 255, 0.18)" : "transparent",
  transition: "background-color 0.15s ease",
  "&:hover": {
    backgroundColor: active ? "rgba(255, 255, 255, 0.22)" : theme.navBtnHover,
  },
});

const MarketPulseAudioPlayer = ({
  audios = [],
  activeIndex = 0,
  onActiveIndexChange = () => {},
}) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [playbackRate, setPlaybackRate] = useState(readStoredPlaybackRate);

  const activeAudio = audios[activeIndex];
  const audioUrl = activeAudio?.url || "";
  const hasMultipleTracks = audios.length > 1;

  const applyAudioSettings = (audio) => {
    if (!audio) {
      return;
    }
    audio.volume = 1;
    audio.muted = false;
    audio.playbackRate = playbackRate;
  };

  useEffect(() => {
    try {
      localStorage.setItem(PLAYBACK_RATE_STORAGE_KEY, String(playbackRate));
    } catch {
      // ignore storage errors
    }
    const audio = audioRef.current;
    if (audio) {
      audio.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  useEffect(() => {
    setIsPlaying(false);
    setIsReady(false);
    setLoadError("");

    const audio = audioRef.current;
    if (!audio || !audioUrl) {
      return undefined;
    }

    applyAudioSettings(audio);
    audio.load();

    return () => {
      audio.pause();
    };
  }, [audioUrl, activeIndex]);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio || !audioUrl) {
      return;
    }

    try {
      applyAudioSettings(audio);

      if (isPlaying || !audio.paused) {
        audio.pause();
        setIsPlaying(false);
        return;
      }

      await audio.play();
      setIsPlaying(true);
      setLoadError("");
    } catch (error) {
      setIsPlaying(false);
      setLoadError(error?.message || "Unable to play audio");
      // eslint-disable-next-line no-console
      console.error("MarketPulse audio play failed:", error, audioUrl);
    }
  };

  const handleSkip = (seconds) => {
    const audio = audioRef.current;
    if (!audio || !audioUrl) {
      return;
    }

    const duration = Number.isFinite(audio.duration) ? audio.duration : 0;
    const nextTime = audio.currentTime + seconds;
    audio.currentTime = Math.max(0, duration ? Math.min(nextTime, duration) : nextTime);
  };

  const handleTrackChange = (direction) => {
    if (!hasMultipleTracks) {
      return;
    }

    if (direction === "next" && activeIndex < audios.length - 1) {
      onActiveIndexChange(activeIndex + 1);
    }
    if (direction === "prev" && activeIndex > 0) {
      onActiveIndexChange(activeIndex - 1);
    }
  };

  const hasPreviousTrack = activeIndex > 0;
  const hasNextTrack = activeIndex < audios.length - 1;

  if (!audios.length) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 1.5, sm: 3.5 },
          width: "100%",
          flexWrap: { xs: "wrap", sm: "nowrap" },
        }}
      >
        <audio
          key={audioUrl}
          ref={audioRef}
          src={audioUrl}
          preload="auto"
          playsInline
          onLoadedData={() => {
            applyAudioSettings(audioRef.current);
            setIsReady(true);
          }}
          onCanPlay={() => setIsReady(true)}
          onEnded={() => setIsPlaying(false)}
          onPause={() => setIsPlaying(false)}
          onPlay={() => setIsPlaying(true)}
          onError={() => {
            setIsReady(false);
            setIsPlaying(false);
            setLoadError("Audio failed to load");
          }}
        />

        <Box
          sx={{
            flex: 1,
            minWidth: { xs: "100%", sm: 220 },
            display: "flex",
            alignItems: "center",
            gap: "2px",
            height: 33,
            overflow: "hidden",
            "@keyframes marketPulseWave": {
              "0%": { transform: "scaleY(0.45)", opacity: 0.55 },
              "100%": { transform: "scaleY(1)", opacity: 1 },
            },
          }}
        >
          {WAVEFORM_BARS.map((height, index) => (
            <Box
              key={index}
              sx={{
                width: 3,
                height: isPlaying ? `${height}px` : `${Math.max(6, height * 0.55)}px`,
                borderRadius: "2px",
                backgroundColor:
                  index === 0 && !isPlaying
                    ? theme.waveformActive
                    : isPlaying
                      ? theme.waveformActive
                      : theme.waveformIdle,
                animation: isPlaying
                  ? `marketPulseWave 0.8s ease-in-out ${(index % 6) * 0.1}s infinite alternate`
                  : "none",
              }}
            />
          ))}
        </Box>

        <Box
          sx={{
            ...speedSegmentSx,
            order: { xs: -1, sm: 0 },
            width: { xs: "100%", sm: "auto" },
            justifyContent: { xs: "flex-end", sm: "flex-start" },
          }}
          role="group"
          aria-label="Playback speed"
        >
          {[1, 2].map((rate) => {
            const active = playbackRate === rate;
            return (
              <Box
                key={rate}
                role="button"
                tabIndex={0}
                aria-pressed={active}
                aria-label={`Playback speed ${rate} times`}
                onClick={() => setPlaybackRate(rate)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setPlaybackRate(rate);
                  }
                }}
                sx={speedOptionSx(active)}
              >
                <Text
                  sx={{
                    fontFamily: "'Inter', 'DM Sans', sans-serif",
                    fontWeight: 600,
                    fontSize: "14px",
                    lineHeight: 1,
                    color: theme.textPrimary,
                  }}
                >
                  {rate}×
                </Text>
              </Box>
            );
          })}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            flexShrink: 0,
            px: { xs: 0, sm: 1.5 },
            width: { xs: "100%", sm: "auto" },
            order: { xs: 0, sm: 0 },
          }}
        >
          {hasMultipleTracks && hasPreviousTrack ? (
            <IconButton
              size="small"
              onClick={() => handleTrackChange("prev")}
              aria-label="Previous audio"
              sx={{
                color: theme.textPrimary,
                backgroundColor: theme.controlBtnBg,
                border: theme.controlBtnBorder,
                width: 32,
                height: 32,
                borderRadius: "12px",
                "&:hover": { backgroundColor: theme.navBtnHover },
              }}
            >
              <ChevronLeftIcon sx={{ fontSize: 18 }} />
            </IconButton>
          ) : null}

          <IconButton
            size="small"
            onClick={() => handleSkip(-10)}
            disabled={!audioUrl || !isReady}
            aria-label="Skip back"
            sx={{
              color: theme.textPrimary,
              backgroundColor: theme.controlBtnBg,
              border: theme.controlBtnBorder,
              width: 45,
              height: 45,
              borderRadius: "14px",
              "&:hover": { backgroundColor: theme.navBtnHover },
              "&.Mui-disabled": { opacity: 0.45 },
            }}
          >
            <SkipPreviousIcon sx={{ fontSize: 20 }} />
          </IconButton>

          <IconButton
            onClick={togglePlay}
            disabled={!audioUrl}
            aria-label={isPlaying ? "Pause" : "Play"}
            sx={{
              background: theme.playGradient,
              color: theme.textPrimary,
              borderRadius: "16px",
              width: 68,
              height: 68,
              boxShadow: theme.playShadow,
              "&:hover": { filter: "brightness(1.05)" },
              "&.Mui-disabled": {
                background: "rgba(43, 127, 255, 0.35)",
                color: "rgba(255,255,255,0.55)",
              },
            }}
          >
            {isPlaying ? (
              <PauseIcon sx={{ fontSize: 28 }} />
            ) : (
              <PlayArrowIcon sx={{ fontSize: 30, ml: 0.25 }} />
            )}
          </IconButton>

          <IconButton
            size="small"
            onClick={() => handleSkip(10)}
            disabled={!audioUrl || !isReady}
            aria-label="Skip forward"
            sx={{
              color: theme.textPrimary,
              backgroundColor: theme.controlBtnBg,
              border: theme.controlBtnBorder,
              width: 45,
              height: 45,
              borderRadius: "14px",
              "&:hover": { backgroundColor: theme.navBtnHover },
              "&.Mui-disabled": { opacity: 0.45 },
            }}
          >
            <SkipNextIcon sx={{ fontSize: 20 }} />
          </IconButton>

          {hasMultipleTracks && hasNextTrack ? (
            <IconButton
              size="small"
              onClick={() => handleTrackChange("next")}
              aria-label="Next audio"
              sx={{
                color: theme.textPrimary,
                backgroundColor: theme.controlBtnBg,
                border: theme.controlBtnBorder,
                width: 32,
                height: 32,
                borderRadius: "12px",
                "&:hover": { backgroundColor: theme.navBtnHover },
              }}
            >
              <ChevronRightIcon sx={{ fontSize: 18 }} />
            </IconButton>
          ) : null}
        </Box>

        {hasMultipleTracks ? (
          <Text sx={{ color: theme.textMuted, fontSize: "10px", minWidth: 28 }}>
            {activeIndex + 1}/{audios.length}
          </Text>
        ) : null}
      </Box>

      {loadError ? (
        <Box
          component="audio"
          controls
          src={audioUrl}
          sx={{ width: "100%", height: 36 }}
        />
      ) : null}
    </Box>
  );
};

export default MarketPulseAudioPlayer;
